import React, { useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { useNavigate, useLocation } from 'react-router-dom';
import { Modal, ConfigProvider, Spin, message } from 'antd';
import SuggestPayerModal from 'components/CredentialingModal/components/SuggestPayerModal';
import { PATH } from 'constants/path';
import { trackHeapEvent } from 'utils/analytics';
import PPAOnboardingHeader from 'components/PPAOnboardingHeader';
import NeedHelp from 'components/NeedHelp';
import { CALENDLY_LINK } from 'constants/common';
import useCredentialInfo from 'hooks/credentialing/useCredentialInfo';
import { getActivePayerList } from 'api/credentialing';
import type { KlarityPayerVo } from 'api/credentialing';
import { STATE } from 'constants/meta';
import { ENPIType } from 'types/credentialing';
import heroImg from './imgs/credentialing-hero.png';
import s from './s.module.less';

// Credentialing landing page —— UI/逻辑移植自设计原型 credentialing tab (#landing)
type TCredTab = 'pricing' | 'roadmap' | 'how' | 'benefits' | 'faqs';

const TABS: { key: TCredTab; label: string }[] = [
    { key: 'pricing', label: 'Pricing' },
    { key: 'roadmap', label: 'Payers' },
    { key: 'how', label: 'How it works' },
    { key: 'benefits', label: 'Why Klarity' },
    { key: 'faqs', label: 'FAQs' },
];

const PRICING_ROWS = [
    { label: 'Credentialing fee', klarity: 'Free', typical: 'About $250 per panel' },
    { label: 'Upfront cost', klarity: '$50 refundable deposit per panel', typical: 'Varies' },
    { label: 'Monthly fee', klarity: 'None', typical: 'Often yes' },
    { label: 'First panels', klarity: 'First 3 deposits back as cashback', typical: 'Full price' },
];

// 每个州默认展示的 payer 数，超出的收进「More payers」（与原型一致）
const PAYER_VISIBLE_COUNT = 10;

const STATE_NAME_BY_CODE: Record<string, string> = STATE.reduce(
    (acc, item) => ({ ...acc, [item.value]: item.label }),
    {} as Record<string, string>,
);

const getStateName = (code: string) => STATE_NAME_BY_CODE[code] || code;

const HOW_STEPS = [
    {
        step: 'Step 1',
        title: 'Submit your info',
        desc: 'Share your credentials and documents. This usually takes about 3 days.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
        ),
    },
    {
        step: 'Step 2',
        title: 'Klarity handles the rest',
        desc: 'We review your file and submit your applications to payers, usually within about 5 days.',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                <rect x="8" y="2" width="8" height="4" rx="1" />
                <path d="M9 14l2 2 4-4" />
            </svg>
        ),
    },
    {
        step: 'Step 3',
        title: 'Get approved',
        desc: "Payers review and approve your applications, which takes about 2 months. Once you're in-network, you can start accepting insurance.",
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M9 12L11 14L15 10M20.6179 5.98434C20.4132 5.99472 20.2072 5.99997 20 5.99997C16.9265 5.99997 14.123 4.84453 11.9999 2.94434C9.87691 4.84446 7.07339 5.99985 4 5.99985C3.79277 5.99985 3.58678 5.9946 3.38213 5.98422C3.1327 6.94783 3 7.95842 3 9.00001C3 14.5915 6.82432 19.2898 12 20.622C17.1757 19.2898 21 14.5915 21 9.00001C21 7.95847 20.8673 6.94791 20.6179 5.98434Z" />
            </svg>
        ),
    },
];

const BENEFITS = [
    {
        theme: 'credBenefitGreen',
        title: 'Under your own NPI',
        desc: 'You get credentialed under your own NPI, not ours. Your payer contracts belong to you.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                <path d="M9 12l2 2 4-4" />
            </svg>
        ),
    },
    {
        theme: 'credBenefitBlue',
        title: 'Keep everything payers pay you',
        desc: "Because you bill under your own NPI, you keep your full reimbursement. Klarity doesn't take a cut of your rates.",
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 6v12M8 10h6a2 2 0 0 1 0 4H9a2 2 0 0 0 0 4h7" />
            </svg>
        ),
    },
    {
        theme: 'credBenefitPeach',
        title: 'Free to credential, free to stay independent',
        desc: 'No credentialing fee and no monthly fee. Your practice, your patients, your contracts.',
        icon: (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 9.9-1" />
            </svg>
        ),
    },
];

const OWN_COLUMNS = ['Grow Therapy', 'Alma', 'Headway', 'SonderMind'];
const OWN_ROWS = [
    {
        label: 'Who owns the payer contracts',
        klarity: 'You do. Contracts sit under your own NPI and Tax ID and stay with your practice.',
        comps: [
            "Contracts sit under the platform's network. If you leave the platform, you can't keep accepting insurance under them.",
            "Contracts sit under the platform's network. If you leave the platform, you can't keep accepting insurance under them.",
            "Contracts sit under the platform's network. If you leave the platform, you can't keep accepting insurance under them.",
            "Contracts sit under the platform's network. If you leave the platform, you can't keep accepting insurance under them.",
        ],
    },
    {
        label: 'Payer coverage',
        klarity: '9 national payer panels plus major state regional panels.',
        comps: [
            "Broad coverage within the platform's network.",
            "Broad coverage within the platform's network.",
            "Broad coverage within the platform's network.",
            "Broad coverage within the platform's network.",
        ],
    },
    {
        label: 'EHR and workflow',
        klarity: 'EHR-agnostic. Use whatever EHR you choose.',
        comps: [
            'Booking, scheduling, payment, and claims run through the Grow Therapy platform.',
            'Insurance billing and payment run through the Alma portal.',
            'Patient experience runs through Headway.',
            'Intake, insurance, payment, and care workflow run through the SonderMind portal.',
        ],
    },
    {
        label: 'Cost structure',
        klarity: 'No subscription or setup fee. You keep your full reimbursement.',
        comps: [
            "Economics depend on the platform's pay rates and terms.",
            'Membership fee applies.',
            "Economics depend on the platform's pay rates and terms.",
            'Provider economics are less publicly transparent.',
        ],
    },
    {
        label: 'Best fit',
        klarity: 'Providers who want to build an independent, insurance-ready private practice they own.',
        comps: [
            'Providers who want fast insurance access through a platform network.',
            'Providers who want fast insurance access through a platform network.',
            'Providers who want fast insurance access through a platform network.',
            'Providers who want fast insurance access through a platform network.',
        ],
    },
];

const FAQS = [
    {
        q: 'Who is this program for?',
        a: 'It is for eligible providers who want to accept insured patients, get payer contracts under their own individual NPI / Tax ID, and avoid managing the full credentialing and claims workflow alone.',
    },
    {
        q: 'Do I have to join the Klarity Marketplace?',
        a: 'No. Credentialing and managed billing can stand alone. Marketplace listing may improve queue priority because it creates active patient demand, but it is optional.',
    },
    {
        q: 'Can I keep cash-pay or private-pay patients?',
        a: 'Yes. The program applies to insured claims you submit through Klarity Managed Billing. Cash-pay and private-pay workflows can continue separately, subject to your agreement and workflow choices.',
    },
    {
        q: 'Do I own my insurance panels?',
        a: 'Yes. You are credentialed under your own individual NPI / Tax ID. The payer contracts carry your name and stay with you. This is not a Klarity group contract.',
    },
    {
        q: 'What happens if I leave Klarity?',
        a: 'You keep your credentials and payer contracts. If you leave before completing the 24-month billing commitment, the early-exit rule may apply for panels credentialed through Klarity.',
    },
    {
        q: 'What is the 24-month commitment?',
        a: 'Providers bill through Klarity for 24 months starting when the first Klarity-managed panel is approved by the payer. This gives Klarity time to recover upfront credentialing work through managed billing, not through credentialing fees.',
    },
    {
        q: 'Is there an early opt-out clause?',
        a: 'Yes. The current rule is a flat $200 per panel credentialed through Klarity if the provider leaves Klarity billing before the 24-month commitment is complete. There is no early-exit fee for panels that were not approved by the payer.',
    },
    {
        q: 'Can I keep my independent practice, business NPI, group practice, or professional corporation?',
        a: "The standard offer is centered on the provider's own individual NPI / Tax ID. Independent practice structures, business NPIs, group practices, professional corporations, and supervision arrangements may be possible, but they need payer-specific review before Klarity can promise a path.",
    },
    {
        q: 'How does Klarity make money from this program?',
        a: "Klarity's program revenue comes from managed billing: 3.9% of paid claims processed through Klarity Managed Billing. Credentialing is the upfront enablement work, not the profit center.",
    },
    {
        q: 'What does credentialing cost?',
        a: 'There is no setup fee or monthly subscription fee for credentialing. Providers pay a $50 per-panel deposit at final documentation submission so Klarity can begin work.',
    },
    {
        q: 'How does the deposit and cashback work?',
        a: 'A card is added in the final documentation step and the $50 per-panel deposit is charged then. Once Klarity starts work, the deposit is non-refundable except through cashback. The current promotion window is July 15-August 31, 2026, and eligible providers receive cashback on the first three panel deposits ($150 = $50 x 3) after the first insurance claim is submitted to Klarity for processing.',
    },
    {
        q: 'What is the 3.9% billing fee?',
        a: 'Klarity charges 3.9% of paid claims processed through Klarity Managed Billing. If a claim pays $100, Klarity keeps $3.90. The fee is based on paid insurance reimbursement, not on a monthly subscription.',
    },
    {
        q: 'Are there other fees, such as per-patient lead fees or platform fees?',
        a: 'The credentialing and billing offer has no setup fee or monthly subscription fee. Klarity marketing, marketplace, or lead-generation fees are separate programs and will be explained separately before you enroll, so you can see the full cost stack.',
    },
    {
        q: 'Which insurance panels can I choose?',
        a: 'Klarity recommends panels based on your state, specialty, payer footprint, reimbursement opportunity, credentialing feasibility, telehealth rules, and patient demand. You can share preferred panels before work begins.',
    },
    {
        q: 'Can I review reimbursement rates before choosing panels?',
        a: 'Klarity will provide available payer and reimbursement guidance where possible, but exact reimbursement can vary by state, provider type, contract, payer rules, and service mix.',
    },
    {
        q: 'Will Klarity credential Medicare, Medicaid, or state-specific programs?',
        a: 'Some government and state programs may be part of the panel roadmap, including Medicare and Medicare Advantage opportunities. Medicaid and state-specific plans depend heavily on state rules, provider type, and payer enrollment requirements.',
    },
    {
        q: 'Can I be credentialed in multiple states?',
        a: 'Yes, if you are licensed and eligible in those states. Recommendations depend on patient demand, payer footprint, specialty, and state-specific requirements.',
    },
    {
        q: 'What if I am already credentialed, or was credentialed years ago?',
        a: 'Tell Klarity which payers you already have. Existing or older credentials may reduce duplicate work, but payer rules decide whether a contract is active, transferable, revalidated, or needs a separate application for your current practice setup.',
    },
    {
        q: 'How long does credentialing take?',
        a: 'Credentialing usually takes 2-3 months, depending on the payer and the completeness of your documentation. Klarity can provide status visibility, but payer approval dates cannot be guaranteed.',
    },
    {
        q: 'What does Klarity Managed Billing include?',
        a: 'Providers complete medical coding and patient insurance details. Klarity processes claims, works denials and delays, posts payments, manages remits, and supports the claim workflow for approved insured visits submitted through Klarity.',
    },
    {
        q: 'Does the 3.9% fee include eligibility checks, prior authorization, secondary claims, copays, and accounts receivable follow-up?',
        a: 'The confirmed core scope includes claims processing, denial and delay follow-up, payment posting, remits, and claim workflow support. Klarity is finalizing the exact launch scope for eligibility verification, prior authorization, secondary insurance, copay collection, and other edge cases.',
    },
    {
        q: 'Can I bill patients I bring myself?',
        a: 'Yes. Provider-owned and Klarity-referred insured patient claims can both count if they are submitted through Klarity Managed Billing and meet the approved workflow requirements.',
    },
    {
        q: "Do I have to use Klarity's EHR, chart in Klarity, or prescribe through Klarity?",
        a: 'No, not by default. Credentialing and billing are intended to be EHR-agnostic. You may keep your current EHR or e-prescribing workflow if the claim can be submitted through the approved Klarity billing workflow with the required coding, insurance, card, and claim details.',
    },
    {
        q: 'What extra effort will be required from me?',
        a: 'You will need to complete credentialing intake, keep CAQH and payer information accurate, submit required documents, select panels, provide patient insurance details, code visits, and follow the approved billing handoff. Klarity handles the payer and claim administration around that workflow.',
    },
    {
        q: 'Do PAs, FNPs, therapists, counselors, psychologists, and PMHNPs qualify?',
        a: 'Eligibility depends on license type, payer rules, specialty, state, supervision requirements, and scope of practice. Klarity will review provider type during intake before confirming which panels can be pursued.',
    },
    {
        q: 'What information will I need to provide?',
        a: 'Klarity will collect the information payers usually need for credentialing and billing setup, such as identity and contact information, license and DEA/CDS details if applicable, NPI, Tax ID or W-9 information, education, specialty, malpractice information, practice locations, work history, payer authorization, disclosures, documents, and signatures.',
    },
    {
        q: 'What if I already have CAQH, or do not have CAQH yet?',
        a: 'You still need to submit required Klarity documentation. An accurate CAQH profile and CAQH access can help Klarity validate information and speed payer work. If you do not have CAQH, tell Klarity during intake so the team can confirm the right workflow before payer submission.',
    },
    {
        q: 'Why do you need sensitive information, and how is it stored?',
        a: 'Payers require sensitive information for identity verification, credentialing, billing setup, and payment. Klarity uses it for credentialing and billing setup and stores it in secure systems, including AWS-backed infrastructure. Final onboarding materials will cover access, retention, and consent.',
    },
];

type Props = {
    isTab?: boolean;
};

export default function PPACredentialingLandingPage(props: Props) {
    const { isTab = false } = props;
    const navigate = useNavigate();
    const location = useLocation();
    const [credentialInfo] = useCredentialInfo();

    // 从向导「Save and exit」返回时提示进度已保存；用完清掉 history state，避免刷新/返回重复弹
    useEffect(() => {
        if ((location.state as { savedExit?: boolean })?.savedExit) {
            message.success('Progress saved. You can resume anytime.');
            // 直接改 history state 清掉标记，避免用 navigate 触发额外渲染影响 toast 展示
            window.history.replaceState({ ...window.history.state, usr: {} }, '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const [activeTab, setActiveTab] = useState<TCredTab>('pricing');

    // 已选择过计费 NPI 类型 = 已进入过流程；此时按钮显示 Resume 并回到对应流程（solo→start 续填，group→group credentialing）
    const npiType = credentialInfo.billingNpiType?.value;
    const provider = credentialInfo.providers[0];
    const submitted = provider?.onboardingStatus === 'COMPLETED';
    const hasStarted = !!npiType && !submitted;
    const ctaLabel = hasStarted ? 'Resume' : 'Get started';
    const [openFaqs, setOpenFaqs] = useState<Record<number, boolean>>({});
    const [showWebinars, setShowWebinars] = useState(false);
    const [suggestOpen, setSuggestOpen] = useState(false);
    // Payers 区块：按 provider 执业州拉取 ACTIVE payer 列表（listActiveUsingPOST）
    const [payers, setPayers] = useState<KlarityPayerVo[]>([]);
    const [payersLoading, setPayersLoading] = useState(true);
    const [activePayerState, setActivePayerState] = useState('');
    const [payersExpanded, setPayersExpanded] = useState(false);
    const [allStatesOpen, setAllStatesOpen] = useState(false);
    const [allPayers, setAllPayers] = useState<KlarityPayerVo[]>([]);
    const [allPayersLoading, setAllPayersLoading] = useState(false);
    const allPayersLoadedRef = useRef(false);
    const sectionRefs = useRef<Partial<Record<TCredTab, HTMLDivElement | null>>>({});
    // tab 点击触发的平滑滚动期间，暂停 scroll-spy 抢占 active
    const scrollLockRef = useRef(false);

    const handleStart = () => {
        trackHeapEvent('WebApp - PPA Credentialing Landing - Click - Get Started', {
            tab: activeTab,
            resume: hasStarted,
        });
        // 续填 group（Practice NPI）直接回 group credentialing；其余回 start 页（start 页据 overview 定位到具体步骤）
        if (hasStarted && npiType === ENPIType.PRACTICE_NPI) {
            navigate(PATH.GROUP_CREDENTIALING);
            return;
        }
        navigate(PATH.CREDENTIALING_START);
    };

    // 点击 tab：高亮并平滑滚动到对应区块
    const handleTabChange = (tab: TCredTab) => {
        setActiveTab(tab);
        trackHeapEvent('WebApp - PPA Credentialing Landing - Click - Tab', { tab });
        scrollLockRef.current = true;
        sectionRefs.current[tab]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.setTimeout(() => {
            scrollLockRef.current = false;
        }, 700);
    };

    // “Sign-up for Webinars” 与右上角 “Webinars & Resources” 行为一致：打开帮助抽屉
    const handleOpenWebinars = () => {
        trackHeapEvent('WebApp - PPA Credentialing Landing - Click - Join Webinars', {});
        setShowWebinars(true);
    };

    const handleOpenSuggest = () => {
        trackHeapEvent('WebApp - PPA Credentialing Landing - Click - Suggest Payer', {});
        setSuggestOpen(true);
    };

    // 按 provider 执业州拉取 ACTIVE payer 列表
    useEffect(() => {
        const states = credentialInfo.states || [];
        if (!states.length) return undefined;
        let stale = false;
        setPayersLoading(true);
        getActivePayerList(states).then((res: any) => {
            if (stale) return;
            setPayersLoading(false);
            if (res?.error) return;
            setPayers(res?.data?.data || []);
        });
        return () => {
            stale = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(credentialInfo.states || []).join(',')]);

    // 滚动时高亮当前区块对应的 tab
    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                if (scrollLockRef.current) return;
                const visible = entries
                    .filter((e) => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
                if (visible) {
                    const key = (visible.target as HTMLElement).dataset.tab as TCredTab;
                    if (key) setActiveTab(key);
                }
            },
            // 顶部留出 sticky header + tabs 的高度
            { rootMargin: '-150px 0px -55% 0px', threshold: 0 },
        );
        Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
        return () => observer.disconnect();
    }, []);

    const toggleFaq = (idx: number) => {
        setOpenFaqs((prev) => ({ ...prev, [idx]: !prev[idx] }));
    };

    const renderPricing = () => (
        <div className={s.credSection}>
            <h3 className={s.credSectionTitle}>Simple, transparent pricing</h3>
            <p className={s.credPriceNote}>
                <span className={s.credPromoTag}>Limited time</span>
                First 3 deposits come back as cashback after your first claim to Klarity.
            </p>
            <table className={s.credPriceTable}>
                <thead>
                    <tr>
                        <th aria-label="metric" />
                        <th className={s.credColKlarity}>
                            Klarity <span className={s.credKlarityTag}>Best for Independent Practice Building</span>
                        </th>
                        <th>Other credentialing platforms</th>
                    </tr>
                </thead>
                <tbody>
                    {PRICING_ROWS.map((row) => (
                        <tr key={row.label}>
                            <th>{row.label}</th>
                            <td className={s.credColKlarity}>{row.klarity}</td>
                            <td className={s.credColTypical}>{row.typical}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // payer 按州分组；chip 顺序跟随 provider 执业州，执业州缺失时回退到接口返回的州
    const providerStates = credentialInfo.states || [];
    const payersByState = payers.reduce<Record<string, string[]>>((acc, item) => {
        if (!item.state || !item.name) return acc;
        acc[item.state] = (acc[item.state] || []).concat(item.name);
        return acc;
    }, {});
    const chipStates = providerStates.length ? providerStates : Object.keys(payersByState);
    const currentPayerState = chipStates.includes(activePayerState) ? activePayerState : chipStates[0] || '';
    const currentStatePayers = payersByState[currentPayerState] || [];
    const visiblePayers = payersExpanded ? currentStatePayers : currentStatePayers.slice(0, PAYER_VISIBLE_COUNT);

    // 不传 states 即返回全部州的 ACTIVE payer；只在首次打开时拉一次
    const handleOpenAllStates = () => {
        setAllStatesOpen(true);
        trackHeapEvent('WebApp - PPA Credentialing Landing - Click - See Payers In Other States');
        if (allPayersLoadedRef.current) return;
        allPayersLoadedRef.current = true;
        setAllPayersLoading(true);
        getActivePayerList().then((res: any) => {
            setAllPayersLoading(false);
            if (res?.error) {
                allPayersLoadedRef.current = false;
                return;
            }
            setAllPayers(res?.data?.data || []);
        });
    };

    const allPayersByState = allPayers.reduce<Record<string, string[]>>((acc, item) => {
        if (!item.state || !item.name) return acc;
        acc[item.state] = (acc[item.state] || []).concat(item.name);
        return acc;
    }, {});
    const allPayerStates = Object.keys(allPayersByState).sort((a, b) => getStateName(a).localeCompare(getStateName(b)));

    const handleSelectPayerState = (code: string) => {
        setActivePayerState(code);
        setPayersExpanded(false);
        trackHeapEvent('WebApp - PPA Credentialing Landing - Click - Payer State', { state: code });
    };

    const renderPayerBody = () => {
        if (payersLoading) {
            return (
                <div className={s.credPayerLoading}>
                    <Spin />
                </div>
            );
        }
        return (
            <>
                <div className={s.credPsStateChips}>
                    {chipStates.map((code) => (
                        <button
                            key={code}
                            type="button"
                            className={cx(s.credPsStateChip, {
                                [s.credPsStateChipActive]: code === currentPayerState,
                            })}
                            onClick={() => handleSelectPayerState(code)}
                        >
                            {getStateName(code)}
                            <span className={s.credPsStateChipCount}>{(payersByState[code] || []).length}</span>
                        </button>
                    ))}
                </div>
                {currentStatePayers.length > 0 ? (
                    <>
                        <div className={s.credPsSectionLabel}>Available now in {getStateName(currentPayerState)}</div>
                        <div className={s.payerStateGroup}>
                            <div className={s.payerStateBody}>
                                <div className={s.credLogoWall}>
                                    {visiblePayers.map((name) => (
                                        <div key={name} className={s.credLogoTile}>
                                            <svg
                                                className={s.credTileCheck}
                                                width="16"
                                                height="16"
                                                viewBox="0 0 16 16"
                                                fill="none"
                                                aria-hidden="true"
                                            >
                                                <circle cx="8" cy="8" r="8" fill="#0E62F4" />
                                                <path
                                                    d="M4.5 8.2l2.1 2.1 4.9-4.9"
                                                    stroke="#fff"
                                                    strokeWidth="1.6"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                />
                                            </svg>
                                            <span className={s.credLogoName}>{name}</span>
                                        </div>
                                    ))}
                                </div>
                                {currentStatePayers.length > PAYER_VISIBLE_COUNT && (
                                    <button
                                        type="button"
                                        className={s.credMoreToggle}
                                        onClick={() => setPayersExpanded((prev) => !prev)}
                                    >
                                        {payersExpanded ? 'Show less' : 'More payers'}
                                    </button>
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    // 该州暂无覆盖：仍鼓励先完善资料，等开通后立即启动
                    <div className={s.credPayerEmpty}>
                        <h4 className={s.credPayerEmptyTitle}>
                            We&apos;re not in {getStateName(currentPayerState)} yet, but we&apos;re expanding
                        </h4>
                        <p className={s.credPayerEmptyDesc}>
                            Klarity doesn&apos;t currently credential payers in {getStateName(currentPayerState)}. You
                            can still complete your profile now, and we&apos;ll start credentialing you the moment we
                            add payers in your state.
                        </p>
                    </div>
                )}
            </>
        );
    };

    const renderPayers = () => (
        <div className={s.credSection}>
            <h3 className={s.credSectionTitle}>Payers available in your states</h3>
            <p className={s.credPriceNote}>
                This is an overview of the insurance panels available in your states.{' '}
                <b>You&apos;ll select your desired panels later in the workflow.</b>
            </p>
            {renderPayerBody()}
            <p className={s.credPayerOtherStates}>
                Practicing in more than one state? Klarity also credentials payers in other states.{' '}
                <a
                    className={s.credPayerOtherStatesLink}
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        handleOpenAllStates();
                    }}
                >
                    See payers in other states &rarr;
                </a>
            </p>
            <div className={s.credRoadmapCta}>
                <button type="button" className={s.ibHeroCta} onClick={handleStart}>
                    {ctaLabel}
                </button>
                <button type="button" className={s.ibHeroCtaSecondary} onClick={handleOpenSuggest}>
                    Suggest a payer
                </button>
            </div>
        </div>
    );

    const renderHow = () => (
        <div className={s.credSection}>
            <h3 className={s.credSectionTitle}>How credentialing works</h3>
            <div className={s.credHowitGrid}>
                {HOW_STEPS.map((item) => (
                    <div key={item.step} className={s.credHowitCard}>
                        <div className={s.credHowitIcon}>{item.icon}</div>
                        <div className={s.credHowitStep}>{item.step}</div>
                        <div className={s.credHowitTitle}>{item.title}</div>
                        <div className={s.credHowitDesc}>{item.desc}</div>
                    </div>
                ))}
            </div>
            <p className={s.credHowitEmail}>
                Questions about credentialing? Email us at{' '}
                <a href="mailto:providersupport@helloklarity.com">providersupport@helloklarity.com</a>
            </p>
        </div>
    );

    const renderBenefits = () => (
        <div className={s.credSection}>
            <h3 className={s.credSectionTitle}>Build a practice you actually own</h3>
            <div className={s.credBenefitsGrid}>
                {BENEFITS.map((b) => (
                    <div key={b.title} className={cx(s.credBenefitCard, s[b.theme])}>
                        <div className={s.credBenefitIcon}>{b.icon}</div>
                        <div>
                            <div className={s.credBenefitTitle}>{b.title}</div>
                            <div className={s.credBenefitDesc}>{b.desc}</div>
                        </div>
                    </div>
                ))}
            </div>
            <div className={s.credOwnScroll}>
                <table className={s.credOwnTable}>
                    <thead>
                        <tr>
                            <th aria-label="metric" />
                            <th className={s.credOwnCol}>
                                Klarity <span className={s.credOwnTag}>Best for Independent Practice Building</span>
                            </th>
                            {OWN_COLUMNS.map((c) => (
                                <th key={c}>{c}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {OWN_ROWS.map((row) => (
                            <tr key={row.label}>
                                <th>{row.label}</th>
                                <td className={s.credOwnCol}>{row.klarity}</td>
                                {row.comps.map((c, i) => (
                                    <td key={OWN_COLUMNS[i]} className={s.credOwnComp}>
                                        {c}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );

    const renderFaqs = () => (
        <div className={s.credSection}>
            <h3 className={s.credSectionTitle}>Frequently asked questions</h3>
            <div>
                {FAQS.map((item, idx) => (
                    <div key={item.q} className={cx(s.credFaqItem, openFaqs[idx] && s.open)}>
                        <div className={s.credFaqQ} onClick={() => toggleFaq(idx)}>
                            {item.q}
                            <svg
                                className={s.chev}
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                            >
                                <polyline points="6 9 12 15 18 9" />
                            </svg>
                        </div>
                        <div className={s.credFaqA}>{item.a}</div>
                    </div>
                ))}
            </div>
        </div>
    );

    const SECTIONS: { key: TCredTab; render: () => React.ReactNode }[] = [
        { key: 'pricing', render: renderPricing },
        { key: 'roadmap', render: renderPayers },
        { key: 'how', render: renderHow },
        { key: 'benefits', render: renderBenefits },
        { key: 'faqs', render: renderFaqs },
    ];

    return (
        <div className={isTab ? s.wrapTab : s.wrap}>
            {!isTab && (
                <div className={s.topbar}>
                    <PPAOnboardingHeader showLogout webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR} />
                </div>
            )}
            <div className={s.content}>
                {/* Hero */}
                <div className={s.credHero}>
                    <div>
                        <p className={s.credHeroAfford}>A $50 deposit you get back. No monthly fees.</p>
                        <h2 className={s.credHeroTitle}>
                            Get credentialed for <span className={s.hl}>free</span>, under{' '}
                            <span className={s.hl}>your own NPI</span>
                        </h2>
                        <p className={s.credHeroSub}>
                            We handle the entire credentialing process so you can start accepting insurance patients
                            faster. Payer contracts belong to you, under your own NPI.
                        </p>
                        <div className={s.credHeroActions}>
                            <button type="button" className={s.ibHeroCta} onClick={handleStart}>
                                {ctaLabel}
                            </button>
                            <button type="button" className={s.ibHeroCtaSecondary} onClick={handleOpenWebinars}>
                                Sign-up for Webinars
                            </button>
                        </div>
                    </div>
                    <div className={s.credHeroArt}>
                        <img src={heroImg} alt="Credentialing" className={s.credHeroImg} />
                    </div>
                </div>

                {/* Tabs */}
                <div className={s.credTabs}>
                    {TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={cx(s.credTab, activeTab === tab.key && s.active)}
                            onClick={() => handleTabChange(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* 所有区块顺序平铺，tab 仅用于滚动定位（与原型 #landing 一致） */}
                {SECTIONS.map(({ key, render }) => (
                    <div
                        key={key}
                        data-tab={key}
                        ref={(el) => {
                            sectionRefs.current[key] = el;
                        }}
                        className={s.credAnchor}
                    >
                        {render()}
                    </div>
                ))}
            </div>
            <NeedHelp
                show={showWebinars}
                onClose={() => setShowWebinars(false)}
                webinarLink={CALENDLY_LINK.KLARITY_BILLING_WEBINAR}
            />

            <SuggestPayerModal
                open={suggestOpen}
                onClose={() => setSuggestOpen(false)}
                states={credentialInfo.states || []}
            />

            <ConfigProvider theme={{ token: { colorPrimary: '#0E62F4' } }}>
                <Modal
                    open={allStatesOpen}
                    onCancel={() => setAllStatesOpen(false)}
                    title="Payers in all states"
                    width={720}
                    footer={null}
                >
                    <p className={s.suggestDesc}>
                        Klarity credentials these insurance panels across all states we currently support. You&apos;ll
                        select your desired panels later in the workflow.
                    </p>
                    {(() => {
                        if (allPayersLoading) {
                            return (
                                <div className={s.credPayerLoading}>
                                    <Spin />
                                </div>
                            );
                        }
                        if (!allPayerStates.length) {
                            return <p className={s.credAllStatesEmpty}>No payers available right now.</p>;
                        }
                        return (
                            <div className={s.credAllStatesBody}>
                                {allPayerStates.map((code) => (
                                    <div key={code} className={s.credAllStatesGroup}>
                                        <div className={s.credPsSectionLabel}>
                                            {getStateName(code)}
                                            <span className={s.credPsStateChipCount}>
                                                {allPayersByState[code].length}
                                            </span>
                                        </div>
                                        <div className={s.credLogoWall}>
                                            {allPayersByState[code].map((name) => (
                                                <div key={`${code}-${name}`} className={s.credLogoTile}>
                                                    <svg
                                                        className={s.credTileCheck}
                                                        width="16"
                                                        height="16"
                                                        viewBox="0 0 16 16"
                                                        fill="none"
                                                        aria-hidden="true"
                                                    >
                                                        <circle cx="8" cy="8" r="8" fill="#0E62F4" />
                                                        <path
                                                            d="M4.5 8.2l2.1 2.1 4.9-4.9"
                                                            stroke="#fff"
                                                            strokeWidth="1.6"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        />
                                                    </svg>
                                                    <span className={s.credLogoName}>{name}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        );
                    })()}
                </Modal>
            </ConfigProvider>
        </div>
    );
}
